// CatchRegistrationModalNavigator.tsx
import * as React from 'react';
import { IconButton, Stack, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CircleIcon from '@mui/icons-material/Circle';

type Props = {
  total: number;
  index: number;
  disabled?: boolean;
  onChange: (index: number) => void;
  ariaLabel?: string;
};

const CatchRegistrationModalNavigator = React.forwardRef<HTMLDivElement, Props>(
  (props, ref) => {
    const { total, index, disabled = false, onChange, ariaLabel = 'Aflaskráning' } = props;

    const mod = React.useCallback(
      (i: number) => (total <= 0 ? 0 : ((i % total) + total) % total),
      [total]
    );

    const go = (i: number) => {
      if (disabled || total === 0) return;
      const next = mod(i);
      if (next !== index) onChange(next);
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled || total === 0) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); e.stopPropagation(); go(index - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); e.stopPropagation(); go(index + 1); }
    };

    return (
      <Stack
        ref={ref}
        direction="row"
        alignItems="center"
        gap={1}
        tabIndex={0}
        onKeyDown={onKeyDown}
        role="group"
        aria-label={ariaLabel}
        sx={{ outline: 'none' }}
      >
        <Tooltip title="Fyrri aflaskráning">
          <span>
            <IconButton
              size="small"
              onClick={() => go(index - 1)}
              disabled={disabled || total === 0}
              sx={{ bgcolor: 'lightslategray', '&:hover': { bgcolor: 'slategray' } }}
              aria-label="Fyrri"
            >
              <ArrowBackIcon sx={{ color: 'white' }} />
            </IconButton>
          </span>
        </Tooltip>

        <Stack direction="row" gap={0.75} justifyContent="center" alignItems="center" aria-label="Vísar">
          {Array.from({ length: total }).map((_, i) => {
            const selected = i === index && !disabled;
            return (
              <IconButton
                key={i}
                size="small"
                onClick={() => go(i)}
                disabled={disabled}
                aria-current={selected ? 'true' : undefined}
                aria-label={`Fara í vigtun #${i + 1}`}
                sx={{ p: 0.5 }}
              >
                <CircleIcon sx={{ fontSize: '0.7em', color: selected ? 'black' : '#CCCCCC' }} />
              </IconButton>
            );
          })}
        </Stack>

        <Tooltip title="Næsta aflaskráning">
          <span>
            <IconButton
              size="small"
              onClick={() => go(index + 1)}
              disabled={disabled || total === 0}
              sx={{ bgcolor: 'lightslategray', '&:hover': { bgcolor: 'slategray' } }}
              aria-label="Næsta"
            >
              <ArrowForwardIcon sx={{ color: 'white' }} />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    );
  }
);

export default CatchRegistrationModalNavigator;
